import type {
  ViewBadgeCelebrationProperties,
  ViewBadgeDetailProperties,
} from '@src/ampli-web-digital'
import { configPublic } from '@src/config.public'

import { getAggregateCount, getNextMilestone, getStepPercentage } from './gamification.util'

const BADGE_GRID_SECTIONS = ['active', 'completed', 'available', 'limited'] as const

type BadgeGridSection = (typeof BADGE_GRID_SECTIONS)[number]

function isBadgeGridSection(view: string): view is BadgeGridSection {
  return BADGE_GRID_SECTIONS.includes(view as BadgeGridSection)
}

export function getBadgeSectionFromView(view?: string): BadgeGridSection | undefined {
  if (!view || !isBadgeGridSection(view)) return undefined
  return view
}

export function getBadgePercentProgress(badge: GamificationApi.Badge): number {
  const nextMilestone = getNextMilestone(badge)
  const aggregateCount = getAggregateCount(badge.count, badge.status, nextMilestone)

  return getStepPercentage(badge.currentCount ?? 0, aggregateCount)
}

// The tracking plan has no track/segment badge type; a cast would emit values it rejects.
function toAmplitudeBadgeType(
  type: GamificationApi.BadgeType
): ViewBadgeDetailProperties['badge type'] {
  return type === 'track' || type === 'segment' ? 'uncategorized' : type
}

export function getBadgeDetailEventProps(badge: GamificationApi.Badge): ViewBadgeDetailProperties {
  return {
    'app name': configPublic.general.appName,
    'badge name': badge.name,
    'badge status': badge.status,
    'badge type': toAmplitudeBadgeType(badge.type),
    'percent progress': getBadgePercentProgress(badge),
    'earned count': badge.type === 'aggregate' ? 1 : (badge.earnedCount ?? 0),
  }
}

export function getBadgeCelebrationEventProps(
  badge: GamificationApi.Badge
): ViewBadgeCelebrationProperties {
  const earnedCount = badge.earnedCount ?? 1

  return {
    'app name': configPublic.general.appName,
    badges: [{ slug: badge.slug, type: badge.type, earnedCount }],
  }
}
